import { Dtos } from "@inrev/common";
import { type FactorGroup } from "@portal/api/decisioning";
import { DropdownInput } from "../../../../form/inputs/DropdownInput";
import { Input } from "../../../../form/inputs/Input";
import { ClickableText, SegmentedControls, Text } from "../../../../primitives";
import { PanelField } from "../PanelField";

type Lens = Dtos.Decisioning.Lens;

export const NEW_GROUP = "__new-group__";

export type NewFactorDraft = {
	name: string;
	lens: Lens;
	/** Chosen group id, `NEW_GROUP`, or null to take the lens's first group. */
	groupId: string | null;
	newGroupName: string;
};

export const EMPTY_NEW_FACTOR: NewFactorDraft = {
	name: "",
	lens: "Character",
	groupId: null,
	newGroupName: "",
};

const LENSES = Dtos.Decisioning.lensSchema.options;

const sortGroups = (groups: readonly FactorGroup[]): FactorGroup[] =>
	[...groups].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

export const groupsForLens = (groups: readonly FactorGroup[], lens: Lens): FactorGroup[] =>
	sortGroups(groups.filter((group) => group.lens === lens));

/** The group in effect: the choice if it still belongs to the lens, else the lens's first group, else a new one. */
export const resolveGroup = (
	draft: NewFactorDraft,
	groups: readonly FactorGroup[],
): FactorGroup | typeof NEW_GROUP => {
	if (draft.groupId === NEW_GROUP) return NEW_GROUP;
	const inLens = groupsForLens(groups, draft.lens);
	return inLens.find((group) => group.id === draft.groupId) ?? inLens[0] ?? NEW_GROUP;
};

export type NewFactorBoxProps = {
	draft: NewFactorDraft;
	onChange: (draft: NewFactorDraft) => void;
	groups: readonly FactorGroup[];
	groupError?: string;
	onBack: () => void;
};

/** The inline "New factor" box. Nothing is saved until the sheet's primary action runs. */
export const NewFactorBox = ({
	draft,
	onChange,
	groups,
	groupError,
	onBack,
}: NewFactorBoxProps) => {
	const lensGroups = groupsForLens(groups, draft.lens);
	const resolved = resolveGroup(draft, groups);
	const groupName = resolved === NEW_GROUP ? draft.newGroupName.trim() || "new" : resolved.name;
	const options = [
		...lensGroups.map((group) => ({ value: group.id, label: group.name })),
		{ value: NEW_GROUP, label: "+ New group" },
	];

	return (
		<div
			className="flex flex-col gap-3 rounded-lg bg-surface-light p-3"
			data-testid="new-factor-box"
		>
			<div className="flex items-center justify-between gap-2">
				<Text type="body-strong" color="primary">
					New factor
				</Text>
				<ClickableText type="caption" color="link" testId="new-factor-back" onClick={onBack}>
					Back to library
				</ClickableText>
			</div>
			<PanelField label="Factor name" required>
				{({ id }) => (
					<Input
						id={id}
						compact
						value={draft.name}
						testId="new-factor-name"
						onChange={(event) => onChange({ ...draft, name: event.target.value })}
					/>
				)}
			</PanelField>
			<PanelField label="Lens" required>
				<SegmentedControls<Lens>
					aria-label="Lens"
					value={draft.lens}
					onChange={(lens) => onChange({ ...draft, lens, groupId: null })}
				>
					{LENSES.map((lens) => (
						<SegmentedControls.Segment key={lens} value={lens}>
							{lens}
						</SegmentedControls.Segment>
					))}
				</SegmentedControls>
			</PanelField>
			<PanelField label="Group" required>
				{({ id, labelId }) => (
					<>
						<DropdownInput
							buttonId={id}
							aria-labelledby={`${labelId} ${id}`}
							compact
							anchor="bottom start"
							value={resolved === NEW_GROUP ? NEW_GROUP : resolved.id}
							options={options}
							testId="new-factor-group"
							onChange={(groupId: string) => onChange({ ...draft, groupId })}
						/>
						{resolved === NEW_GROUP && (
							<Input
								compact
								aria-label="New group name"
								placeholder="Group name"
								value={draft.newGroupName}
								error={groupError !== undefined}
								testId="new-factor-new-group-name"
								onChange={(event) => onChange({ ...draft, newGroupName: event.target.value })}
							/>
						)}
						{groupError && (
							<Text
								type="caption"
								className="text-intent-danger-text"
								role="alert"
								data-testid="new-factor-group-error"
							>
								{groupError}
							</Text>
						)}
					</>
				)}
			</PanelField>
			<Text type="caption" color="tertiary" className="font-normal">
				Also saved to the {groupName} group for future accounts.
			</Text>
		</div>
	);
};
